import { ChatMessageType } from "../contracts";
import { isRecord } from "../utils/objectValueConversion";
import type { ChatMessageViewModel } from "./chatMessageViewModel";
import type { CommercialPlanData } from "./commericalPlanModels";

export type AgentResponse = {
    type: "tool_result";
    tool_use_id: string;
};

export type CommercialSimulatorAgentResponse = AgentResponse & CommercialPlanData;
export type CommercialSimulatorSubBrandAgentResponse = AgentResponse & CommercialPlanData;

export const isCommercialPlanData = (payload: unknown): payload is CommercialPlanData => {
    if (!isRecord(payload)) {
        return false;
    }

    return (
        payload.type === "tool_result"
        && typeof payload.tool_use_id === "string"
    );
};

export const isAgentResponse = (type: ChatMessageType, payload: unknown): payload is AgentResponse => {
    if (!isRecord(payload) || type !== ChatMessageType.AgentResponse) {
        return false;
    }
    return (
        payload.type === "tool_result"
        && typeof payload.tool_use_id === "string"
    );
};

export const getLastAgentResponse = (
    messages: ChatMessageViewModel[] | undefined
): ChatMessageViewModel | undefined => {
    if (!messages) {
        return undefined;
    }

    for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];

        if (message.type === ChatMessageType.AgentResponse) {
            return message;
        }
    }

    return undefined;
};
export type AgentResponseResult<TToolResponse extends AgentResponse> = {
    chatConversationMessageId: number;
    payload: TToolResponse;
};

export const getAgentResponseById = <TToolResponse extends AgentResponse>(
    messages: ChatMessageViewModel[] | null | undefined,
    toolRequestId: string | null | undefined
): AgentResponseResult<TToolResponse> | null => {
    if (!messages || !toolRequestId) {
        return null;
    }

    for (const message of messages) {
        if (!message.id) {
            continue;
        }

        const payload = message.payload;

        if (isAgentResponse(message.type, payload) && payload.tool_use_id === toolRequestId) {
            return {
                chatConversationMessageId: message.id,
                payload: payload as TToolResponse
            };
        }
    }

    return null;
};

export const isCommercialSimulatorSubBrandAgentResponse = (
    type: ChatMessageType,
    action: string | null | undefined,
    payload: unknown
): payload is CommercialSimulatorSubBrandAgentResponse => {
    return (
        isAgentResponse(type, payload)
        && action === "commercial_simulator_sub_brand"
    );
};

export const isCommercialSimulatorAgentResponse = (
    type: ChatMessageType,
    action: string | null | undefined,
    payload: unknown
): payload is CommercialSimulatorAgentResponse => {
    return (
        isAgentResponse(type, payload)
        && action === "commercial_simulator_sku"
    );
};
